import type { I18n } from 'vue-i18n'
import { createI18n, Translation } from 'vue-i18n'
import en_GB from './locales/en-gb.json' with { type: 'json' }
import type { App, ComputedRef, Ref } from 'vue'
import { computed, inject, ref } from 'vue'
import type { TranslationFragment } from '@/graphql/api'
import { type Locale } from './types'

export type AppTranslator = {
    i18n: AppI18nPlugin
    translator: AppI18nPlugin['global']
    setMessages: SetMessagesFunction
    locale: Readonly<Ref<Locale>>
    setLocale(locale: Locale): void
    integerFormatter: Readonly<Ref<Intl.NumberFormat>>
    getCountryName(countryCode: string): string
    getCurrencyName(currencyCode: string): string
    getCurrencySymbol(currencyCode: string): string
}

export type { Locale }

export type SetMessagesFunction = (
    locale: Locale,
    messages: TranslationFragment[]
) => Promise<void>
export type AppI18nPlugin = I18n<
    Record<string, unknown>,
    Record<string, unknown>,
    Record<string, unknown>,
    Locale,
    false
>

export interface MessageCatalogue {
    [key: string]: string | MessageCatalogue
}

const appTranslatorKey = 'app.plugin.translator'

export function createI18nPlugin(
    app: App,
    options: {
        defaultLocale: Locale
        fallbackLocale: Locale
    }
): AppTranslator {
    const locale: Ref<Locale> = ref(options.defaultLocale)

    const i18n = createI18n({
        legacy: false,
        locale: options.defaultLocale,
        fallbackLocale: options.fallbackLocale,
        messages: {
            [options.defaultLocale]: flattenTranslations(en_GB),
        },
        datetimeFormats: {},
        numberFormats: {},
        globalInjection: true,
    })

    app.component('I18n', Translation)

    const translator = i18n.global

    const setMessages: SetMessagesFunction = async (
        locale,
        messages
    ): Promise<void> => {
        return requireMessages(locale).then((catalogue) => {
            translator.setLocaleMessage(locale, {
                ...catalogue,
                ...Object.fromEntries(
                    messages.map((t) => [t.token, t.translation])
                ),
            })
        })
    }

    const displayRegion =
        typeof Intl.DisplayNames !== 'undefined'
            ? computed(() => {
                  return new Intl.DisplayNames([locale.value], {
                      type: 'region',
                  })
              })
            : undefined

    const displayCurrency =
        typeof Intl.DisplayNames !== 'undefined'
            ? computed(() => {
                  return new Intl.DisplayNames([locale.value], {
                      type: 'currency',
                  })
              })
            : undefined

    const integerFormatter: ComputedRef<Intl.NumberFormat> = computed(
        (): Intl.NumberFormat => {
            return new Intl.NumberFormat(locale.value, {
                style: 'decimal',
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
            })
        }
    )

    const pluginData: AppTranslator = {
        i18n,
        translator,
        setMessages,
        locale,
        integerFormatter,
        setLocale(l) {
            locale.value = l
            i18n.global.locale.value = l
        },
        getCountryName(countryCode) {
            return displayRegion?.value.of(countryCode) ?? countryCode
        },
        getCurrencyName(currencyCode) {
            return displayCurrency?.value.of(currencyCode) ?? currencyCode
        },
        getCurrencySymbol(currencyCode) {
            return (
                new Intl.NumberFormat(locale.value, {
                    style: 'currency',
                    currency: currencyCode,
                    currencyDisplay: 'narrowSymbol',
                })
                    .formatToParts(0)
                    .find((part) => part.type === 'currency')?.value ??
                currencyCode
            )
        },
    }

    const plugin = Object.freeze(pluginData)

    app.provide(appTranslatorKey, plugin)

    return plugin
}

export function useTranslator(): AppTranslator {
    return inject(appTranslatorKey) as AppTranslator
}

export async function requireMessages(
    locale: Locale
): Promise<FlatMessageCatalogue> {
    return doRequireMessages(locale).then(flattenTranslations)
}

function doRequireMessages(locale: Locale): Promise<MessageCatalogue> {
    switch (locale) {
        case 'en-au':
            return import('./locales/en-au.json')
        case 'en-ca':
            return import('./locales/en-ca.json')
        case 'en-gb':
            return Promise.resolve(en_GB)
        case 'en-ie':
            return import('./locales/en-ie.json')
        case 'en-nz':
            return import('./locales/en-nz.json')
        case 'en-us':
            return import('./locales/en-us.json')
        case 'en-za':
            return import('./locales/en-za.json')
        default:
            return Promise.resolve(en_GB)
    }
}

type FlatMessageCatalogue = Record<string, string>

function flattenTranslations(
    catalogue: MessageCatalogue
): Record<string, string> {
    return Object.entries(catalogue).reduce(
        (acc, curr) => {
            if (typeof curr[1] === 'string') {
                return {
                    ...acc,
                    [curr[0]]: curr[1],
                }
            }

            return {
                ...acc,
                ...Object.fromEntries(
                    Object.entries(flattenTranslations(curr[1])).map((v) => {
                        return [`${curr[0]}.${v[0]}`, v[1]]
                    })
                ),
            }
        },
        {} as Record<string, string>
    )
}

if (import.meta.vitest) {
    const { expect, describe, test } = import.meta.vitest

    describe('flattenTranslations', () => {
        test('All in one', () => {
            expect(
                flattenTranslations({
                    single_key: 'some val',
                    globals: {
                        working_hours: '{from} to {to}',
                    },
                    gallery: {
                        swipe_to_rotate: 'Swipe to rotate',
                        deep: {
                            deeper: 'value',
                        },
                    },
                })
            ).toStrictEqual({
                single_key: 'some val',
                'globals.working_hours': '{from} to {to}',
                'gallery.swipe_to_rotate': 'Swipe to rotate',
                'gallery.deep.deeper': 'value',
            })
        })
    })
}
